import React, { useCallback, useMemo, useState } from 'react'
import { Table, Typography, Switch } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'
import type { GetPaymentMetricsResponseDto } from '../../../api/dto/get-payment-assignment-metrics.dto'
import type {
  PaymentAssignmentUserDto,
  PaymentAssignmentUserRole,
} from '../../../api/dto/get-payment-assignment-users.dto'
import { sortUsersByRoleThenName } from '../hooks/usePaymentAssignmentUserOptions'
import {
  PAYMENT_ASSIGNMENT_STATUS_LABELS,
  PaymentAssignmentStatusId,
} from '../Utils/types'
import {
  UNPAID_STATUSES,
  BOT_STATUSES,
  TICKET_STATUSES,
  PRIORITY_STATUSES,
  PRIORITY_LEVELS,
  PRIORITY_LEVEL_LABELS,
  DEFAULT_STATUSES,
} from './payment-assignment-metrics.config'
import { PaymentAssignmentRole } from '../Utils/roles'

const { Text } = Typography

const fmt = (n: number) => n.toLocaleString()

interface PaymentMetricsProps {
  data: GetPaymentMetricsResponseDto | undefined
  fromDate?: dayjs.Dayjs
  toDate?: dayjs.Dayjs
  paymentDate?: dayjs.Dayjs
  loading?: boolean
  // Controlled state (lifted to parent for URL persistence)
  showAllMakers: boolean
  onShowAllMakersChange: (v: boolean) => void
  showAllEscalation: boolean
  onShowAllEscalationChange: (v: boolean) => void
  /** Map of sectionId → expanded boolean for all 6 payment-tab sections. */
  sectionExpanded: Record<string, boolean>
  onSectionExpandChange: (id: string, expanded: boolean) => void
}

interface StatusRow {
  key: string
  label: string
  isGrandTotal?: boolean
  [status: string]: string | number | boolean | undefined
}

const SectionCard = styled.div`
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #fafafa;
  }
`

const ExpandLink = styled.span`
  color: #1890ff;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`

function mergeStatuses(
  defaultStatuses: readonly string[],
  perEntityMetrics: Record<string, Record<string, number>>,
  totalMetrics: Record<string, number>
): string[] {
  const seen = new Set<string>(defaultStatuses)
  const extra: string[] = []
  for (const counts of Object.values(perEntityMetrics)) {
    for (const s of Object.keys(counts)) {
      if (!seen.has(s)) {
        seen.add(s)
        extra.push(s)
      }
    }
  }
  for (const s of Object.keys(totalMetrics)) {
    if (!seen.has(s)) {
      seen.add(s)
      extra.push(s)
    }
  }
  return [...defaultStatuses, ...extra]
}

function buildColumns(statuses: string[]): ColumnsType<StatusRow> {
  const cols: ColumnsType<StatusRow> = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left' as const,
      width: 160,
      render: (text: string, record: StatusRow) =>
        record.isGrandTotal ? <Text strong>{text}</Text> : text,
    },
  ]

  for (const status of statuses) {
    const label =
      PAYMENT_ASSIGNMENT_STATUS_LABELS[status as PaymentAssignmentStatusId] ??
      status.replace(/_/g, ' ')
    cols.push({
      title: label.toUpperCase(),
      dataIndex: status,
      key: status,
      align: 'center' as const,
      width: 170,
      render: (value: number | undefined, record: StatusRow) => {
        const num = value ?? 0
        return record.isGrandTotal ? <Text strong>{fmt(num)}</Text> : fmt(num)
      },
    })
  }

  cols.push({
    title: 'TOTAL',
    key: '__total__',
    fixed: 'right' as const,
    align: 'center' as const,
    width: 120,
    render: (_: unknown, record: StatusRow) => {
      const total = statuses.reduce(
        (sum, s) => sum + (Number(record[s]) || 0),
        0
      )
      return record.isGrandTotal ? <Text strong>{fmt(total)}</Text> : fmt(total)
    },
  })

  return cols
}

function getUsersByRoles(
  usersById: Record<number, PaymentAssignmentUserDto>,
  roles: PaymentAssignmentUserRole[]
): PaymentAssignmentUserDto[] {
  const roleSet = new Set<string>(roles)
  return Object.values(usersById).filter((u) => u.role && roleSet.has(u.role))
}

function buildRows(
  perEntityMetrics: Record<string, Record<string, number>>,
  totalMetrics: Record<string, number>,
  statuses: string[],
  roleUsers: PaymentAssignmentUserDto[],
  entityLabel: string,
  roleOrder: string[],
  defaultEntityKeys?: readonly string[],
  defaultEntityLabels?: Record<string, string>
): StatusRow[] {
  const sortedRoleUsers = sortUsersByRoleThenName(roleUsers, roleOrder)
  const knownIds = new Set(sortedRoleUsers.map((u) => String(u.id)))
  const defaultKeys = new Set(defaultEntityKeys ?? [])
  const addedKeys = new Set<string>()

  const rows: StatusRow[] = []

  // Role users from usersById
  for (const user of sortedRoleUsers) {
    const counts = perEntityMetrics[String(user.id)] ?? {}
    const baseName = user.name ?? `${entityLabel} #${user.id}`
    const label = user.role === 'CHECKER' ? `${baseName} (CHECKER)` : baseName
    const row: StatusRow = {
      key: String(user.id),
      label,
    }
    for (const s of statuses) {
      row[s] = counts[s] ?? 0
    }
    rows.push(row)
    addedKeys.add(String(user.id))
  }

  // Extra keys from perEntityMetrics not in usersById and not in defaultEntityKeys
  for (const [entityKey, counts] of Object.entries(perEntityMetrics)) {
    if (
      knownIds.has(entityKey) ||
      addedKeys.has(entityKey) ||
      defaultKeys.has(entityKey)
    )
      continue
    const row: StatusRow = {
      key: entityKey,
      label: entityKey,
    }
    for (const s of statuses) {
      row[s] = counts[s] ?? 0
    }
    rows.push(row)
  }

  // Pinned default rows (e.g. Unassigned, Autopay) — always shown last
  for (const key of defaultEntityKeys ?? []) {
    const counts = perEntityMetrics[key] ?? {}
    const row: StatusRow = {
      key,
      label: defaultEntityLabels?.[key] ?? key,
    }
    for (const s of statuses) {
      row[s] = counts[s] ?? 0
    }
    rows.push(row)
  }

  const grandTotal: StatusRow = {
    key: 'grand-total',
    label: 'Grand Total',
    isGrandTotal: true,
  }
  for (const s of statuses) {
    grandTotal[s] = totalMetrics[s] ?? 0
  }
  rows.push(grandTotal)

  return rows
}

function buildLabeledRows(
  perEntityMetrics: Record<string, Record<string, number>>,
  statuses: string[],
  labelMap: Record<string, string>,
  entityKeys: readonly string[]
): StatusRow[] {
  const rows: StatusRow[] = entityKeys.map((entityKey) => {
    const counts = perEntityMetrics[entityKey] ?? {}
    const row: StatusRow = {
      key: entityKey,
      label: labelMap[entityKey] ?? entityKey,
    }
    for (const s of statuses) {
      row[s] = counts[s] ?? 0
    }
    return row
  })

  const grandTotal: StatusRow = {
    key: 'grand-total',
    label: 'Grand Total',
    isGrandTotal: true,
  }
  for (const s of statuses) {
    let sum = 0
    for (const ek of entityKeys) {
      sum += perEntityMetrics[ek]?.[s] ?? 0
    }
    grandTotal[s] = sum
  }
  rows.push(grandTotal)

  return rows
}

function CollapsibleSection({
  title,
  children,
  defaultExpanded = true,
  extra,
  expanded: controlledExpanded,
  onExpandChange,
}: {
  title: string
  children: React.ReactNode
  defaultExpanded?: boolean
  extra?: React.ReactNode
  /** When provided together with onExpandChange, the component is fully controlled. */
  expanded?: boolean
  onExpandChange?: (expanded: boolean) => void
}) {
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded)
  const isControlled =
    controlledExpanded !== undefined && onExpandChange !== undefined
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded

  const toggle = useCallback(() => {
    if (isControlled) {
      onExpandChange && onExpandChange(!expanded)
    } else {
      setUncontrolledExpanded((prev) => !prev)
    }
  }, [isControlled, expanded, onExpandChange])

  return (
    <SectionCard>
      <SectionHeader onClick={toggle}>
        <Text strong style={{ fontSize: 15 }}>
          {title}
        </Text>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 16 }}
          onClick={(e) => e.stopPropagation()}
        >
          {extra}
          <ExpandLink onClick={toggle}>
            {expanded ? (
              <>
                <DownOutlined /> Collapse
              </>
            ) : (
              <>
                <RightOutlined /> Expand
              </>
            )}
          </ExpandLink>
        </div>
      </SectionHeader>
      {expanded && children}
    </SectionCard>
  )
}

function StatusMetricsTable({
  perEntityMetrics = {},
  totalMetrics = {},
  usersById,
  roleFilters,
  entityLabelMap,
  entityKeys,
  defaultEntityKeys,
  defaultEntityLabels,
  defaultStatuses,
  strictStatuses,
  entityColumnTitle,
  loading,
  hideInactive = false,
}: {
  perEntityMetrics?: Record<string, Record<string, number>>
  totalMetrics?: Record<string, number>
  usersById?: Record<number, PaymentAssignmentUserDto>
  roleFilters?: PaymentAssignmentUserRole[]
  entityLabelMap?: Record<string, string>
  entityKeys?: readonly string[]
  defaultEntityKeys?: readonly string[]
  defaultEntityLabels?: Record<string, string>
  defaultStatuses: readonly string[]
  strictStatuses?: boolean
  entityColumnTitle: string
  loading?: boolean
  hideInactive?: boolean
}) {
  const roleUsers = useMemo(
    () =>
      usersById && roleFilters?.length
        ? getUsersByRoles(usersById, roleFilters)
        : [],
    [usersById, roleFilters]
  )

  const statuses = useMemo(
    () =>
      strictStatuses
        ? [...defaultStatuses]
        : mergeStatuses(defaultStatuses, perEntityMetrics, totalMetrics),
    [strictStatuses, defaultStatuses, perEntityMetrics, totalMetrics]
  )

  const columns = useMemo(() => {
    const cols = buildColumns(statuses)
    if (cols.length > 0) {
      cols[0].title = entityColumnTitle
    }
    return cols
  }, [statuses, entityColumnTitle])

  const rows = useMemo(
    () =>
      entityLabelMap && entityKeys
        ? buildLabeledRows(
            perEntityMetrics,
            statuses,
            entityLabelMap,
            entityKeys
          )
        : buildRows(
            perEntityMetrics,
            totalMetrics,
            statuses,
            roleUsers,
            entityColumnTitle,
            roleFilters ?? [],
            defaultEntityKeys,
            defaultEntityLabels
          ),
    [
      entityLabelMap,
      entityKeys,
      defaultEntityKeys,
      defaultEntityLabels,
      perEntityMetrics,
      totalMetrics,
      statuses,
      roleUsers,
      entityColumnTitle,
      roleFilters,
    ]
  )

  const visibleRows = useMemo(() => {
    if (!hideInactive) return rows
    return rows.filter((row) => {
      if (row.isGrandTotal) return true
      const total = statuses.reduce((sum, s) => sum + (Number(row[s]) || 0), 0)
      return total !== 0
    })
  }, [rows, statuses, hideInactive])

  return (
    <Table<StatusRow>
      columns={columns}
      dataSource={visibleRows}
      pagination={false}
      loading={loading}
      scroll={{ x: 'max-content' }}
      size="small"
      bordered
      rowClassName={(record) => (record.isGrandTotal ? 'grand-total-row' : '')}
    />
  )
}

function KeyValueMetricsTable({
  metrics,
  defaultKeys,
  labelMap,
  labelColumnTitle = 'Status',
  loading,
}: {
  metrics: Record<string, number>
  defaultKeys?: readonly string[]
  labelMap?: Record<string, string>
  labelColumnTitle?: string
  loading?: boolean
}) {
  const dataSource = useMemo(() => {
    const orderedKeys = defaultKeys
      ? Array.from(
          new Set([
            ...defaultKeys,
            ...Object.keys(metrics).filter((k) => !defaultKeys.includes(k)),
          ])
        )
      : Object.keys(metrics)

    const rows: { key: string; label: string; count: number }[] =
      orderedKeys.map((k) => ({
        key: k,
        label: labelMap?.[k] ?? k.replace(/_/g, ' '),
        count: metrics[k] ?? 0,
      }))

    const total = orderedKeys.reduce((sum, k) => sum + (metrics[k] ?? 0), 0)
    rows.push({ key: '__total__', label: 'Total', count: total })
    return rows
  }, [metrics, defaultKeys, labelMap])

  const columns: ColumnsType<{ key: string; label: string; count: number }> = [
    {
      title: labelColumnTitle,
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record) =>
        record.key === '__total__' ? <Text strong>{text}</Text> : text,
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'right' as const,
      width: 120,
      render: (value: number, record) =>
        record.key === '__total__' ? (
          <Text strong>{fmt(value)}</Text>
        ) : (
          fmt(value)
        ),
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      loading={loading}
      size="small"
      bordered
    />
  )
}

const UNPAID_LABELS: Record<string, string> = Object.fromEntries(
  UNPAID_STATUSES.map((s) => [
    s,
    PAYMENT_ASSIGNMENT_STATUS_LABELS[s as PaymentAssignmentStatusId] ??
      s.replace(/_/g, ' '),
  ])
)

const BOT_LABELS: Record<string, string> = Object.fromEntries(
  BOT_STATUSES.map((s) => [
    s,
    PAYMENT_ASSIGNMENT_STATUS_LABELS[s as PaymentAssignmentStatusId] ??
      s.replace(/_/g, ' '),
  ])
)

function buildUserLabelMap(
  usersById: Record<number, PaymentAssignmentUserDto>
): Record<string, string> {
  return Object.fromEntries(
    Object.values(usersById).map((u) => [String(u.id), u.name ?? String(u.id)])
  )
}

const MAKER_DEFAULT_ENTITY_KEYS = ['Unassigned', 'Autopay'] as const

const MAKER_DEFAULT_ENTITY_LABELS: Record<string, string> = {
  Unassigned: 'Unassigned',
  Autopay: 'Autopay',
}

export default function PaymentMetrics({
  data,
  loading,
  fromDate: _fromDate,
  toDate: _toDate,
  paymentDate,
  showAllMakers,
  onShowAllMakersChange,
  showAllEscalation,
  onShowAllEscalationChange,
  sectionExpanded,
  onSectionExpandChange,
}: PaymentMetricsProps) {
  const usersById = data?.usersById ?? {}
  const emptyRecord: Record<string, Record<string, number>> = {}
  const emptyTotals: Record<string, number> = {}

  const makeShowAllToggle = (
    checked: boolean,
    onChange: (v: boolean) => void
  ) => (
    <span
      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
    >
      <Switch size="small" checked={checked} onChange={onChange} />
      Show all
    </span>
  )

  const makersToggle = makeShowAllToggle(showAllMakers, onShowAllMakersChange)
  const escalationToggle = makeShowAllToggle(
    showAllEscalation,
    onShowAllEscalationChange
  )

  const ticketUserLabels = useMemo(
    () => buildUserLabelMap(usersById),
    [usersById]
  )

  const ticketUserKeys = useMemo(
    () => Object.keys(data?.ticketMetrics ?? {}),
    [data?.ticketMetrics]
  )

  return (
    <div>
      <CollapsibleSection
        title="Count of Makers (Activity log)"
        extra={makersToggle}
        expanded={sectionExpanded['makers']}
        onExpandChange={(v) => onSectionExpandChange('makers', v)}
      >
        <StatusMetricsTable
          perEntityMetrics={data?.statusMakerMetrics ?? emptyRecord}
          totalMetrics={data?.totalMakerMetrics ?? emptyTotals}
          usersById={usersById}
          roleFilters={[
            PaymentAssignmentRole.MAKER,
            PaymentAssignmentRole.CHECKER,
          ]}
          defaultEntityKeys={MAKER_DEFAULT_ENTITY_KEYS}
          defaultEntityLabels={MAKER_DEFAULT_ENTITY_LABELS}
          defaultStatuses={DEFAULT_STATUSES}
          entityColumnTitle="Maker"
          loading={loading}
          hideInactive={!showAllMakers}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Count of Escalation (Activity log)"
        extra={escalationToggle}
        expanded={sectionExpanded['escalation']}
        onExpandChange={(v) => onSectionExpandChange('escalation', v)}
      >
        <StatusMetricsTable
          perEntityMetrics={data?.statusEscalatorMetrics ?? emptyRecord}
          totalMetrics={data?.totalEscalatorMetrics ?? emptyTotals}
          usersById={usersById}
          roleFilters={[
            PaymentAssignmentRole.ESCALATOR,
            PaymentAssignmentRole.CHECKER,
          ]}
          defaultStatuses={DEFAULT_STATUSES}
          entityColumnTitle="Escalator"
          loading={loading}
          hideInactive={!showAllEscalation}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Count of Priority"
        expanded={sectionExpanded['priority']}
        onExpandChange={(v) => onSectionExpandChange('priority', v)}
      >
        <StatusMetricsTable
          perEntityMetrics={data?.priorityMetrics ?? emptyRecord}
          entityLabelMap={PRIORITY_LEVEL_LABELS}
          entityKeys={PRIORITY_LEVELS}
          defaultStatuses={PRIORITY_STATUSES}
          strictStatuses
          entityColumnTitle="Priority Type"
          loading={loading}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Ticket Count"
        expanded={sectionExpanded['ticket']}
        onExpandChange={(v) => onSectionExpandChange('ticket', v)}
      >
        <StatusMetricsTable
          perEntityMetrics={data?.ticketMetrics ?? emptyRecord}
          entityLabelMap={ticketUserLabels}
          entityKeys={ticketUserKeys}
          defaultStatuses={TICKET_STATUSES}
          strictStatuses
          entityColumnTitle="Ticket"
          loading={loading}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Count of Bot Status"
        expanded={sectionExpanded['botstat']}
        onExpandChange={(v) => onSectionExpandChange('botstat', v)}
      >
        <KeyValueMetricsTable
          metrics={data?.statusBotMetrics ?? emptyTotals}
          defaultKeys={BOT_STATUSES}
          labelMap={BOT_LABELS}
          loading={loading}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title={`Count of Unpaid${paymentDate ? ` (${paymentDate.format('MMMM YYYY')})` : ''}`}
        expanded={sectionExpanded['unpaid']}
        onExpandChange={(v) => onSectionExpandChange('unpaid', v)}
      >
        <KeyValueMetricsTable
          metrics={data?.pendingCount ?? emptyTotals}
          defaultKeys={UNPAID_STATUSES}
          labelMap={UNPAID_LABELS}
          loading={loading}
        />
      </CollapsibleSection>
    </div>
  )
}
